import _asyncToGenerator from "/jove-dashboard/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/asyncToGenerator.js";
import _regeneratorRuntime from "/jove-dashboard/node_modules/next/dist/compiled/@babel/runtime/regenerator/index.js";

/**
 * Security parameter helpers for the Google login flow (Authorization Code Flow with PKCE).
 *
 * Flow overview:
 * 1. Before requesting the Google authorization URL, call `prepareGoogleLoginSecurityParams()` to generate
 *    the `state` (CSRF protection), the `nonce` (ID token replay protection) and the PKCE `codeChallenge`
 *    (derived from a random `codeVerifier`). The `state`, `nonce` and `codeVerifier` are persisted in
 *    `sessionStorage` so they survive the redirect to Google and back.
 * 2. After Google redirects back to the app, call `validateGoogleLoginCallbackState()` with the `state`
 *    from the callback URL to make sure the response belongs to a login attempt started by this browser
 *    tab, then call `consumeGoogleLoginSecurityParams()` to read (and clear) the stored `codeVerifier` /
 *    `nonce` for the token exchange and ID token validation.
 *
 * Reference: https://auth0.com/docs/get-started/authentication-and-authorization-flow/authorization-code-flow-with-pkce
 */

/** sessionStorage key holding the security params of the in-flight Google login attempt */
export var JOVE_GOOGLE_LOGIN_SECURITY_PARAMS_KEY = 'JOVE_GOOGLE_LOGIN_SECURITY_PARAMS';
/** a login attempt is abandoned when the callback arrives after this TTL */

var GOOGLE_LOGIN_SECURITY_PARAMS_TTL = 10 * 60 * 1000;
/** characters allowed in the PKCE code_verifier (RFC 7636 section 4.1) */

var PKCE_CODE_VERIFIER_CHARSET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~';

var getSessionStorage = function getSessionStorage() {
  if (false) {
    return undefined;
  }

  return window.sessionStorage;
};
/** generate a cryptographically random string from the PKCE unreserved character set */


export var generateRandomToken = function generateRandomToken() {
  var length = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 43;
  var randomValues = new Uint32Array(length);
  window.crypto.getRandomValues(randomValues);
  return Array.from(randomValues, function (value) {
    return PKCE_CODE_VERIFIER_CHARSET[value % PKCE_CODE_VERIFIER_CHARSET.length];
  }).join('');
};

var base64UrlEncode = function base64UrlEncode(buffer) {
  var binaryString = '';
  new Uint8Array(buffer).forEach(function (_byte) {
    binaryString += String.fromCharCode(_byte);
  });
  return window.btoa(binaryString).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};
/** generate the OAuth `state` used to correlate the callback with this login attempt (CSRF protection) */


export var generateGoogleLoginState = function generateGoogleLoginState() {
  return generateRandomToken(32);
};
/** generate the OIDC `nonce` used to mitigate ID token replay attacks */

export var generateGoogleLoginNonce = function generateGoogleLoginNonce() {
  return generateRandomToken(32);
};
/** generate the PKCE code_verifier (43-128 characters, RFC 7636) */

export var generatePkceCodeVerifier = function generatePkceCodeVerifier() {
  return generateRandomToken(64);
};
/** derive the PKCE code_challenge from a code_verifier with the S256 method: BASE64URL(SHA256(verifier)) */

export var generatePkceCodeChallenge = /*#__PURE__*/function () {
  var _ref = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(codeVerifier) {
    var _window$crypto;

    var digest;
    return _regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            if ((_window$crypto = window.crypto) !== null && _window$crypto !== void 0 && _window$crypto.subtle) {
              _context.next = 2;
              break;
            }

            throw new Error('PKCE code challenge generation requires a secure browser context (HTTPS or localhost)');

          case 2:
            _context.next = 4;
            return window.crypto.subtle.digest('SHA-256', new TextEncoder().encode(codeVerifier));

          case 4:
            digest = _context.sent;
            return _context.abrupt("return", base64UrlEncode(digest));

          case 6:
          case "end":
            return _context.stop();
        }
      }
    }, _callee);
  }));

  return function generatePkceCodeChallenge(_x) {
    return _ref.apply(this, arguments);
  };
}();
/** remove the stored security params, e.g. when the login attempt fails or is cancelled */

export var clearGoogleLoginSecurityParams = function clearGoogleLoginSecurityParams() {
  var _getSessionStorage;

  (_getSessionStorage = getSessionStorage()) === null || _getSessionStorage === void 0 ? void 0 : _getSessionStorage.removeItem(JOVE_GOOGLE_LOGIN_SECURITY_PARAMS_KEY);
};
/** read the stored security params of the in-flight Google login attempt */

export var getGoogleLoginSecurityParams = function getGoogleLoginSecurityParams() {
  var storage = getSessionStorage();

  if (!storage) {
    return undefined;
  }

  try {
    var rawParams = storage.getItem(JOVE_GOOGLE_LOGIN_SECURITY_PARAMS_KEY);

    if (!rawParams) {
      return undefined;
    }

    var storedParams = JSON.parse(rawParams);

    if (!(storedParams !== null && storedParams !== void 0 && storedParams.state) || !(storedParams !== null && storedParams !== void 0 && storedParams.codeVerifier) || Date.now() - storedParams.createdAt > GOOGLE_LOGIN_SECURITY_PARAMS_TTL) {
      clearGoogleLoginSecurityParams();
      return undefined;
    }

    return {
      state: storedParams.state,
      nonce: storedParams.nonce,
      codeVerifier: storedParams.codeVerifier
    };
  } catch (_) {
    return undefined;
  }
};
/**
 * generate a fresh set of Google login security params and persist them so the callback can be validated;
 * returns the values to send to the backend authorize endpoint
 */

export var prepareGoogleLoginSecurityParams = /*#__PURE__*/function () {
  var _ref2 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee2() {
    var _getSessionStorage2;

    var state, nonce, codeVerifier, codeChallenge, storedParams;
    return _regeneratorRuntime.wrap(function _callee2$(_context2) {
      while (1) {
        switch (_context2.prev = _context2.next) {
          case 0:
            state = generateGoogleLoginState();
            nonce = generateGoogleLoginNonce();
            codeVerifier = generatePkceCodeVerifier();
            _context2.next = 5;
            return generatePkceCodeChallenge(codeVerifier);

          case 5:
            codeChallenge = _context2.sent;
            storedParams = {
              state: state,
              nonce: nonce,
              codeVerifier: codeVerifier,
              createdAt: Date.now()
            };
            (_getSessionStorage2 = getSessionStorage()) === null || _getSessionStorage2 === void 0 ? void 0 : _getSessionStorage2.setItem(JOVE_GOOGLE_LOGIN_SECURITY_PARAMS_KEY, JSON.stringify(storedParams));
            return _context2.abrupt("return", {
              state: state,
              nonce: nonce,
              codeChallenge: codeChallenge
            });

          case 9:
          case "end":
            return _context2.stop();
        }
      }
    }, _callee2);
  }));

  return function prepareGoogleLoginSecurityParams() {
    return _ref2.apply(this, arguments);
  };
}();
/** check that the `state` returned by the Google callback matches the in-flight login attempt of this tab */

export var validateGoogleLoginCallbackState = function validateGoogleLoginCallbackState(callbackState) {
  var storedParams = getGoogleLoginSecurityParams();
  return !!callbackState && !!(storedParams !== null && storedParams !== void 0 && storedParams.state) && callbackState === storedParams.state;
};
/** read and clear the stored security params; call only after the callback state has been validated */

export var consumeGoogleLoginSecurityParams = function consumeGoogleLoginSecurityParams() {
  var securityParams = getGoogleLoginSecurityParams();
  clearGoogleLoginSecurityParams();
  return securityParams;
};